import { CAPACITY_WEEKS_MAX } from '@asas/domain/capacity';
import { addWeeks } from '@asas/domain/iso-week';
import type {
  ExternSummeZelle,
  FachgruppenSummeZelle,
  FachgruppenSummen,
  GesamtSummeZelle,
  IsoWeek,
} from '@asas/domain';

import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { FENSTER_SCHRITT, stundenAnzeige } from '../wochenraster/wochenraster';

/**
 * Reine Hilfen fuer „Auslastung je Fachgruppe" (AC-407) — ohne React, ohne Netz.
 *
 * Gerechnet wird hier NICHTS: Summen, Kapazitaet, Ueberlast und Prozent kommen
 * fertig vom Server (Kernregel 35); Stunden bleiben Zeichenketten (Regel 10),
 * die Anzeige tauscht nur den Dezimalpunkt (`stundenAnzeige`).
 */

/** Die Breite des Fensters: das Ladefenster-Maximum (ADR-07). */
export const AUSLASTUNG_WOCHEN = CAPACITY_WEEKS_MAX;

export function auslastungFensterVerschieben(von: IsoWeek, schritte: number): IsoWeek {
  return addWeeks(von, schritte * FENSTER_SCHRITT);
}

type Auslastungswert = Extract<FachgruppenSummeZelle, { status: 'auswertbar' }>['auslastung'];

/** Die Auslastung wie der Server sie liefert; nie selbst gerechnet. */
export function auslastungText(wert: Auslastungswert): string {
  switch (wert.zustand) {
    case 'prozent':
      return `${String(wert.prozent)} %`;
    case 'abwesend':
      return wert.konflikt ? 'abwesend · Plan trotz Abwesenheit' : 'abwesend';
    case 'keine_kapazitaet':
      return wert.konflikt ? 'keine Kapazität · Plan ohne Kapazität' : 'keine Kapazität';
  }
}

export type SummenZustand = 'leer' | 'zu_klein' | 'ueberlast' | 'auswertbar';

export interface SummenAnzeige {
  readonly zustand: SummenZustand;
  readonly text: string;
  /** Kleiner Zusatz („eigene Gruppe"), leer wenn keiner. */
  readonly hinweis: string;
  readonly title: string;
}

export function zuKleinText(schwelle: number): string {
  return `unter ${String(schwelle)} Personen`;
}

const LEER_TITLE = 'keine Mitglieder in dieser Woche';

/** Eine Zelle einer Fachgruppe oder der Gesamtsumme. */
export function summenAnzeige(
  zelle: FachgruppenSummeZelle | GesamtSummeZelle,
  schwelle: number,
): SummenAnzeige {
  if (zelle.status === 'leer') {
    return { zustand: 'leer', text: '0 h', hinweis: '', title: LEER_TITLE };
  }

  if (zelle.status === 'zu_klein') {
    return { zustand: 'zu_klein', text: zuKleinText(schwelle), hinweis: '', title: '' };
  }

  const teile = [
    `${stundenAnzeige(zelle.plannedHours)} / ${stundenAnzeige(zelle.availableHours)} h`,
    auslastungText(zelle.auslastung),
  ];

  if (zelle.overload) teile.push('Überlast');

  return {
    zustand: zelle.overload ? 'ueberlast' : 'auswertbar',
    text: teile.join(' · '),
    hinweis: 'ausnahme' in zelle && zelle.ausnahme ? 'eigene Gruppe' : '',
    title:
      `${String(zelle.personen)} Personen · Soll ${stundenAnzeige(zelle.targetHours)} h` +
      ` · Abwesenheit ${stundenAnzeige(zelle.absenceHours)} h` +
      ` · verfügbar ${stundenAnzeige(zelle.availableHours)} h`,
  };
}

/** Eine Zelle der Externen-Zeile: nur Stunden, nie Prozent oder Kapazitaet. */
export function externAnzeige(zelle: ExternSummeZelle, schwelle: number): SummenAnzeige {
  if (zelle.status === 'zu_klein') {
    return { zustand: 'zu_klein', text: zuKleinText(schwelle), hinweis: '', title: '' };
  }

  return {
    zustand: 'auswertbar',
    text: `${stundenAnzeige(zelle.plannedHours)} h`,
    hinweis: '',
    title: 'Planstunden Externer, ohne Kapazität',
  };
}

// --- Laden: das juengste Fenster gewinnt, das vorige wird abgebrochen -----------

export type SummenAbruf = (
  from: IsoWeek,
  optionen: { readonly signal: AbortSignal },
) => Promise<FachgruppenSummen>;

export type LadeAusgang =
  | { readonly art: 'geladen'; readonly summen: FachgruppenSummen }
  | { readonly art: 'fehler'; readonly fehler: unknown }
  | { readonly art: 'ueberholt' };

/**
 * Ein Lader je Ansicht. Jedes `laden` bricht den vorigen Aufruf ab (der
 * `AbortSignal` wirkt, weil die Prozedur in `EIGENE_LINK_KETTE` steht) und
 * meldet fuer ein ueberholtes Fenster `ueberholt` — auch dann, wenn dessen
 * Antwort trotzdem eintrifft oder der Abbruch als Fehler zurueckkommt.
 */
export function erzeugeSummenLader(abruf: SummenAbruf): (from: IsoWeek) => Promise<LadeAusgang> {
  let laufend: AbortController | null = null;

  return async (from) => {
    laufend?.abort();
    const abbruch = new AbortController();
    laufend = abbruch;

    try {
      const summen = await abruf(from, { signal: abbruch.signal });

      return abbruch.signal.aborted ? { art: 'ueberholt' } : { art: 'geladen', summen };
    } catch (fehler) {
      return abbruch.signal.aborted ? { art: 'ueberholt' } : { art: 'fehler', fehler };
    }
  };
}

export type OeffnenAusgang = LadeAusgang | { readonly art: 'modul_aus' };

export const MODUL_AUS_TEXT = 'Die Ressourcenplanung ist für diesen Mandanten nicht aktiv.';

/**
 * Das erste Oeffnen: erst `tenant.settings`; ist das Modul `ressourcenplanung`
 * abgeschaltet, wird `auswertungen.fachgruppenSummen` NICHT abgerufen.
 */
export async function oeffneAuslastung(
  modulAktiv: () => Promise<boolean>,
  lade: (from: IsoWeek) => Promise<LadeAusgang>,
  from: IsoWeek,
): Promise<OeffnenAusgang> {
  try {
    if (!(await modulAktiv())) return { art: 'modul_aus' };
  } catch (fehler) {
    return { art: 'fehler', fehler };
  }

  return await lade(from);
}

// --- Zustand der Ansicht: rein, damit er ohne DOM testbar ist ------------------

export interface AuslastungStand {
  readonly summen: FachgruppenSummen | null;
  readonly fehler: FehlerBefund | null;
  /** Modul abgeschaltet: keine Navigation, stattdessen wieder „Auslastung öffnen". */
  readonly modulAus: boolean;
}

export const ANFANGSSTAND: AuslastungStand = { summen: null, fehler: null, modulAus: false };

/**
 * Wie ein Ausgang den Stand aendert. Ein ueberholtes Fenster aendert nichts;
 * ein Fehler laesst die geladene Tabelle stehen (Regel „Ansichten"), ein
 * Erfolg raeumt ihn.
 */
export function naechsterStand(alt: AuslastungStand, ausgang: OeffnenAusgang): AuslastungStand {
  switch (ausgang.art) {
    case 'ueberholt':
      return alt;
    case 'modul_aus':
      return { summen: null, fehler: null, modulAus: true };
    case 'geladen':
      return { summen: ausgang.summen, fehler: null, modulAus: false };
    case 'fehler':
      return {
        summen: alt.summen,
        fehler: klassifiziereFehler(ausgang.fehler, { schreibzugriff: false }),
        modulAus: false,
      };
  }
}

/**
 * Oeffnen prueft das Modul (einmal); Fensterwechsel rufen direkt den Lader,
 * der den vorigen Aufruf abbricht — die Reihenfolge folgt den Klicks, nicht
 * der Antwort eines nicht abbrechbaren `tenant.settings`.
 */
export function erzeugeAuslastungSteuerung(
  modulAktiv: () => Promise<boolean>,
  abruf: SummenAbruf,
): {
  readonly oeffnen: (from: IsoWeek) => Promise<OeffnenAusgang>;
  readonly wechseln: (from: IsoWeek) => Promise<OeffnenAusgang>;
} {
  const lade = erzeugeSummenLader(abruf);

  return {
    oeffnen: async (from) => await oeffneAuslastung(modulAktiv, lade, from),
    wechseln: async (from) => await lade(from),
  };
}
